import { useQuotationReportItemsByQuotation } from '@/core/domain/stock/Quotations/Quotations.hooks';
import { Button, Card, Form, Input, Space, Table } from 'antd';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table';
import { ReportQuotationItemsByQuotationItem } from '@/core/domain/stock/Quotations/Quotations.types';
import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import SupplierSelect from '@/components/Suppliers/SupplierSelect';
import ReportQuotationsHeader from '@/components/Reports/ReportPrivate/ReportQuotations/ReportQuotationsHeader';

import '../quotation.styles.less';
import formatterPrice from '@/utils/number/formatterPrice';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

type SearchParams = Record<'quotationId', string | undefined>;

type Item = ReportQuotationItemsByQuotationItem & {
  unitPrice: number;
  suppliedQuantity: number;
};

const PrivateQuatationReportsItemsByQuotation: React.FC = () => {
  const match = useRouteMatch();
  const { search } = useLocation();
  const params = Object.fromEntries(
    new URLSearchParams(search),
  ) as SearchParams;
  const listRef = useRef<HTMLDivElement>(null);

  const [items, setItems] = useState<Item[]>([]);

  const { data, isLoading, dataUpdatedAt } = useQuotationReportItemsByQuotation(
    params.quotationId ? Number(params.quotationId) : undefined,
  );

  useEffect(() => {
    if (data?.result?.items) {
      setItems(
        data?.result?.items.map((item) => ({
          ...item,
          unitPrice: 0,
          suppliedQuantity: 0,
        })),
      );

      document.title = `Itens-da-cotação_${data?.result?.company.name
        ?.split(' ')
        .join('-')}`;
    }
  }, [dataUpdatedAt]);

  function handlePrintNotValue() {
    listRef.current?.classList.add('remove-values');

    setTimeout(() => {
      window.print();

      if (listRef.current?.classList.contains('remove-values')) {
        listRef.current.classList.remove('remove-values');
      }
    }, 500);
  }

  function handleUpdate(path: [index: number, field: string], value: unknown) {
    setItems((state) =>
      state.map((item, index) =>
        index === path[0] ? { ...item, [path[1]]: value } : item,
      ),
    );
  }

  const columns: ColumnsType<Item> = [
    {
      dataIndex: 'code',
      title: 'Código',
      render: (code) => code ?? '-',
    },
    {
      dataIndex: 'description',
      title: 'Descrição',
      ellipsis: true,
    },
    {
      dataIndex: 'requestedQuantity',
      title: 'Quantidade',
      align: 'right',
      render: (value) => formatterPrice(value),
    },
    {
      dataIndex: 'suppliedQuantity',
      title: 'Qtde. fornecida',
      align: 'right',
      width: '15%',
      render: (value, _, index) => (
        <div className="fieldValue">
          <InputDecimal
            style={{ width: '90%' }}
            min={0}
            value={value}
            onChange={(nextValue) =>
              handleUpdate([index, 'suppliedQuantity'], nextValue)
            }
            className="table-value"
          />
        </div>
      ),
    },
    {
      dataIndex: 'unitPrice',
      title: 'Valor unitário($)',
      align: 'right',
      width: '15%',
      render: (value, _, index) => (
        <div className="fieldValue">
          <InputDecimal
            style={{ width: '90%' }}
            min={0}
            value={value}
            onChange={(nextValue) =>
              handleUpdate([index, 'unitPrice'], nextValue)
            }
            className="table-value"
          />
        </div>
      ),
    },
    {
      title: 'Valor total($)',
      align: 'right',
      width: '15%',
      render: (_, row) => (
        <div className="fieldValue">
          <span className="table-value">
            {formatterPrice((row.suppliedQuantity || 0) * (row.unitPrice || 0))}
          </span>
        </div>
      ),
    },
  ];

  return (
    <Suspense fallback={<>Carregando...</>}>
      {match.isExact && !isLoading && !!data && (
        <TemplateReport>
          <main className="reportQuotation">
            <Space direction="vertical" className="w-100" size="large">
              <ReportQuotationsHeader
                company={data?.result?.company}
                title="Itens da Cotação"
              />

              <Card>
                <Group position="between">
                  <Form
                    className="form-field"
                    initialValues={{ code: data?.result?.quotationId }}
                    key={data?.result?.quotationId}
                  >
                    <Group gap={24}>
                      <FloatingFormItem name="code" label="Cotação">
                        <Input disabled style={{ width: 150 }} />
                      </FloatingFormItem>

                      <FloatingFormItem name="supplierId" label="Fornecedor">
                        <SupplierSelect style={{ width: 300 }} />
                      </FloatingFormItem>
                    </Group>
                  </Form>

                  <Group gap={16}>
                    <Button
                      className="button-print"
                      onClick={handlePrintNotValue}
                    >
                      PDF sem valores
                    </Button>
                    <Button
                      type="primary"
                      className="button-print"
                      onClick={() => window.print()}
                    >
                      <span className="text-color-white">PDF com valores</span>
                    </Button>
                  </Group>
                </Group>

                <div ref={listRef}>
                  <Table
                    className="m-t-20"
                    dataSource={items}
                    columns={columns}
                    pagination={false}
                    scroll={{ x: true }}
                  />
                </div>
              </Card>

              <section className="reportQuotation-observation">
                <p>
                  OBS: Os dados preenchidos na tabela não serão salvos na base
                  de dados.
                </p>
              </section>
            </Space>
          </main>
        </TemplateReport>
      )}
    </Suspense>
  );
};

export default PrivateQuatationReportsItemsByQuotation;
